import React, { useState, useRef, useEffect } from 'react';
import useScrollAnimation from '../hooks/useScrollAnimation';

const NewsletterSection = () => {
  const { ref: sectionRef, animationClass } = useScrollAnimation('fade-in-up', 0.1);
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const containerRef = useRef(null);
  const animationRef = useRef(null);

  // Automatic slider animation
  useEffect(() => {
    if (hasInteracted) return; // Stop animation once user interacts

    let direction = 1; // 1 for right, -1 for left
    let currentPos = 50;

    const animate = () => {
      currentPos += direction * 0.2; // Speed of animation (slower)

      // Reverse direction at boundaries
      if (currentPos >= 70) {
        direction = -1;
      } else if (currentPos <= 30) {
        direction = 1;
      }

      setSliderPosition(currentPos);
      animationRef.current = requestAnimationFrame(animate);
    };

    // Start animation after a brief delay
    const timeoutId = setTimeout(() => {
      animationRef.current = requestAnimationFrame(animate);
    }, 500);

    return () => {
      clearTimeout(timeoutId);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [hasInteracted]);

  const handleMove = (e) => {
    if (!containerRef.current) return;

    // Stop automatic animation on first interaction
    if (!hasInteracted) {
      setHasInteracted(true);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    }

    const rect = containerRef.current.getBoundingClientRect();
    const x = e.type.includes('mouse') ? e.clientX : e.touches[0].clientX;
    const position = ((x - rect.left) / rect.width) * 100;

    if (position >= 0 && position <= 100) {
      setSliderPosition(position);
    }
  };

  const handleMouseDown = () => {
    setIsDragging(true);
    if (!hasInteracted) {
      setHasInteracted(true);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      handleMove(e);
    }
  };

  return (
    <section ref={sectionRef} className={`bg-white py-8 sm:py-12 md:py-16 lg:py-24 px-3 sm:px-4 md:px-6 ${animationClass}`}>
      <div className="max-w-screen-xl mx-auto">
        {/* Section Header - Responsive */}
        <div className="text-center mb-6 sm:mb-8 md:mb-10 lg:mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-serif font-bold text-gray-900 leading-tight mb-2 sm:mb-3 md:mb-4">
            Before <span className="bg-[#E85D44] text-[#FFD93D] px-3 sm:px-4 md:px-6 py-0.5 sm:py-1 md:py-2 inline-block">&</span> After
          </h2>
          <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed px-2">
            Slide to see your lamp transform from <strong>lights off</strong> to <strong>lights on</strong>
          </p>
        </div>

        {/* Before/After Slider Container - Responsive aspect ratio */}
        <div className="max-w-2xl mx-auto">
          <div
            ref={containerRef}
            className="relative aspect-[4/5] sm:aspect-[3/4] md:aspect-[4/5] rounded-xl sm:rounded-2xl overflow-hidden shadow-xl sm:shadow-2xl cursor-ew-resize select-none"
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleMouseDown}
            onTouchEnd={handleMouseUp}
            onTouchMove={handleMove}
          >
            {/* After Image (Lights ON) - Background */}
            <div className="absolute inset-0">
              <img
                src="/images/lamp-lights-on.jpg"
                alt="Night Lamp - Lights On"
                className="w-full h-full object-cover"
                loading="lazy"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              {/* Label */}
              <div className="absolute top-2 sm:top-4 md:top-6 right-2 sm:right-4 md:right-6 bg-[#E85D44] text-white px-2 sm:px-4 md:px-6 py-1 sm:py-2 md:py-3 rounded-full font-semibold text-[10px] sm:text-xs md:text-sm uppercase tracking-wider shadow-lg">
                Lights ON
              </div>
            </div>

            {/* Before Image (Lights OFF) - Foreground with clip */}
            <div
              className="absolute inset-0 overflow-hidden"
              style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
            >
              <img
                src="/images/lamp-lights-off.jpg"
                alt="Night Lamp - Lights Off"
                className="w-full h-full object-cover"
                loading="lazy"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              {/* Label */}
              <div className="absolute top-2 sm:top-4 md:top-6 left-2 sm:left-4 md:left-6 bg-gray-700 text-white px-2 sm:px-4 md:px-6 py-1 sm:py-2 md:py-3 rounded-full font-semibold text-[10px] sm:text-xs md:text-sm uppercase tracking-wider shadow-lg">
                Lights OFF
              </div>
            </div>

            {/* Slider Handle */}
            <div
              className="absolute top-0 bottom-0 w-0.5 sm:w-1 bg-white cursor-ew-resize shadow-lg"
              style={{ left: `${sliderPosition}%` }}
            >
              {/* Handle Circle */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 bg-white rounded-full shadow-2xl flex items-center justify-center border-2 sm:border-3 md:border-4 border-[#E85D44]">
                {/* Left Arrow */}
                <svg
                  className="w-2 h-2 sm:w-3 sm:h-3 md:w-4 md:h-4 text-[#E85D44] absolute left-1 sm:left-1.5 md:left-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                {/* Right Arrow */}
                <svg
                  className="w-2 h-2 sm:w-3 sm:h-3 md:w-4 md:h-4 text-[#E85D44] absolute right-1 sm:right-1.5 md:right-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </div>

            {/* Instruction Text - Shows before interaction */}
            {!hasInteracted && (
              <div className="absolute bottom-2 sm:bottom-4 md:bottom-8 left-1/2 -translate-x-1/2 bg-black/70 text-white px-3 sm:px-4 md:px-6 py-1.5 sm:py-2 md:py-3 rounded-full text-[10px] sm:text-xs md:text-sm font-medium animate-pulse pointer-events-none">
                👆 Drag to compare
              </div>
            )}
          </div>

          {/* Helper Text Below */}
          <div className="flex justify-between items-center mt-3 sm:mt-4 md:mt-6 text-gray-600 px-2">
            <div className="flex items-center gap-1.5 sm:gap-2">
              <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-4 md:h-4 bg-gray-700 rounded-full"></div>
              <span className="text-[10px] sm:text-xs md:text-sm font-medium">Lights Off</span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <span className="text-[10px] sm:text-xs md:text-sm font-medium">Lights On</span>
              <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-4 md:h-4 bg-[#E85D44] rounded-full"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewsletterSection;
